import { useEffect, useState } from "react";
import api, { fmtDate, formatApiError } from "@/lib/apiClient";
import EmployeeLayout from "@/layouts/EmployeeLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function MyLeave() {
  const [bal, setBal] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ leave_type: "Annual Leave", start_date: "", end_date: "", half_day: false, note: "" });

  const load = () => {
    api.get("/leave-balance").then((r) => setBal(r.data));
    api.get("/leave-requests").then((r) => setReqs(r.data));
    api.get("/leave-types").then((r) => setTypes(r.data));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.start_date || !form.end_date) { toast.error("Select dates"); return; }
    try { await api.post("/leave-requests", form); toast.success("Leave request submitted"); setOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cancel = async (id) => { await api.post(`/leave-requests/${id}/cancel`); toast.success("Cancelled"); load(); };

  return (
    <EmployeeLayout title="Leave">
      {bal && (
        <div className="bg-slate-900 text-white rounded-md p-5 mb-4">
          <div className="text-[11px] uppercase tracking-widest text-zinc-400">Annual leave remaining</div>
          <div className="num text-4xl font-black text-amber-500">{bal.remaining} <span className="text-lg text-zinc-400">days</span></div>
          <div className="grid grid-cols-3 gap-2 mt-4 text-center">
            <div><div className="num font-bold">{bal.entitlement}</div><div className="text-[10px] uppercase text-zinc-400">Entitlement</div></div>
            <div><div className="num font-bold">{bal.taken}</div><div className="text-[10px] uppercase text-zinc-400">Taken</div></div>
            <div><div className="num font-bold">{bal.pending_days}</div><div className="text-[10px] uppercase text-zinc-400">Pending</div></div>
          </div>
        </div>
      )}
      <Button data-testid="request-leave-btn" onClick={() => setOpen(true)} className="w-full h-12 bg-amber-500 text-black font-bold hover:bg-amber-600 mb-4">Request leave</Button>

      <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">My requests</h2>
      <div className="space-y-2">
        {reqs.length === 0 && <div className="text-sm text-zinc-400">No requests yet.</div>}
        {reqs.map((r) => (
          <div key={r.id} data-testid={`leave-${r.id}`} className="bg-white border border-zinc-200 rounded-md p-3">
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm">{r.leave_type}</span>
              <StatusBadge status={r.status} />
            </div>
            <div className="num text-xs text-zinc-500 mt-1">{fmtDate(r.start_date)} – {fmtDate(r.end_date)} · {r.working_days} day(s)</div>
            {r.status === "pending" && <button onClick={() => cancel(r.id)} className="text-xs text-rose-600 mt-2">Cancel request</button>}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Request leave</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Leave type</Label>
              <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                <SelectTrigger data-testid="leave-type" className="h-12 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start</Label><Input data-testid="leave-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="h-12 mt-1" /></div>
              <div><Label>End</Label><Input data-testid="leave-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="h-12 mt-1" /></div>
            </div>
            <div><Label>Note</Label><Textarea data-testid="leave-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
            <Button data-testid="leave-submit" onClick={submit} className="w-full h-12 bg-slate-900 text-white">Submit request</Button>
          </div>
        </DialogContent>
      </Dialog>
    </EmployeeLayout>
  );
}
